import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import OutsideClickHandler from 'react-outside-click-handler';
import { NavLink, Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { findIndex, includes } from 'ramda';
import * as Animations from '@components/@core/Animations';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Avatar from '@components/@core/Avatar';
import Icon from '@components/@core/Icon';
import AppActions from '~/actions/app';
import { convertUrl } from '~/services/util';

const ProfileHeader = ({
    avatar = null,
    user = { id: 0 },
    company = { id: 0 },
    banner = null,
    routes = { tabs: [], options: [] },
    query = '',
    editable = false,
    rootPath,
    type = 'company',
    title = 'DrivingSales',
    description = 'Followed by: 104',
    onLogin = () => {},
    onChange = () => {},
    onSelect = () => {},
    onConnect = () => {},
}) => {
    const authenticated = useSelector((state) => state.app.token);
    const loginUser = useSelector((state) => state.app.user?.community_user);
    const isSelf = loginUser?.id === user?.id;
    const [opened, setOpened] = useState(false);
    const params = useParams();

    const dispatch = useDispatch();
    useEffect(() => {
        if (authenticated && !isSelf) {
            dispatch(AppActions.getentityconnectionsRequest());
        }
    }, []);

    const getClass = (base, index) => {
        const current = findIndex((e) => e.name === params.tab, routes.tabs);
        return classNames('section', base === params.tab && 'active', current - 1 === index && 'before');
    };

    const handleCog = (e) => () => {
        setOpened(false);
    };

    const handleAvatarClick = (e) => {
        e.stopPropagation();
        
        if (editable) {
            onChange('avatar')();
        }
    };

    const getPendingState = () => {
        const pendingConnections = loginUser.connections ? [...loginUser.connections?.pending_connections_received, ...loginUser.connections?.pending_connections_sent] : [];
        if (type === 'employee') {
            const idx = findIndex((x) => x.connector?.user_id === user?.id, pendingConnections);
            return idx > -1;
        }
        const idx = findIndex((x) => x.connector?.id === company?.id, pendingConnections);
        return idx > -1;
    };

    const getConnectedState = () => {
        const activeConnections = loginUser.connections?.active_user_connections || [];
        const followCompanies = loginUser.connections?.following_companies || [];
        if (type === 'employee') {
            const idx = findIndex((x) => x.connector?.user_id === user?.id, activeConnections);
            return idx > -1;
        }
        const idx = findIndex((x) => x.connector?.id === company?.id, followCompanies);
        return idx > -1;
    };

    const avatarURL = convertUrl(avatar, type === 'company' ? '/images/default_company.svg' : '/images/default.png');
    const bannerURL = convertUrl(banner, type === 'company' ? '/images/bg-company-cover.png' : '/images/employee-banner.png');
    const connected = authenticated ? (!isSelf ? getConnectedState() : true) : false;
    const pending = authenticated && !isSelf ? getPendingState() : false;
    const isSponsored = type === 'employee' || (company?.data?.sponsored_level && company?.data?.sponsored_level !== 'wiki');

    return (
        <div className="profile-header">
            {editable && <div className="profile-header-overlay" />}
            <div 
                className="top-section" 
                style={{ backgroundImage: `url(${bannerURL})` }}
                onClick={editable ? onChange('cover') : null}
            >
                <div className="info-bar d-flex ssm-column">
                    <div className="ml-3" onClick={(e) =>handleAvatarClick(e)} >
                        <Avatar className="avatar-large no-shadow" url={avatarURL} name={title} isSponsored={isSponsored} size="large" type="logo" />
                    </div>
                    <div className="main-info">
                        <p className="dsl-w28 mb-2 text-400">{title}</p>
                        <p className="dsl-w14">{description}</p>
                    </div>
                    <div className="social-info">
                        <div>
                            {authenticated ? (
                                <>
                                    {pending ? (
                                        <p className="dsl-w16">Connection request has been sent...</p>
                                    ) : (
                                        <>{!connected && <Button className="btn-connect" type="medium" name="CONNECT" onClick={onConnect} />}</>
                                    )}
                                </>
                            ) : (
                                <div className="d-flex ai-center login-info">
                                    <Button className="login-button me-2" name="Login to Connect" onClick={() => onLogin()} />
                                </div>
                            )}
                        </div>
                        <div className="d-h-end">
                        </div>
                    </div>
                </div>
                {isSelf && (
                    <div className="dots-menu">
                        <EditDropdown iconColor="#fff" options={['Edit']} onChange={onSelect} />
                    </div>
                )}
            </div>
            <div className="header-nav">
                {routes.tabs.map((item, index) => (
                    <div className={getClass(item.name, index)} key={`route${index}`}>
                        <NavLink to={`${rootPath}/${item.name}${query}`} className="section-item dsl-d14">
                            {item.label}
                        </NavLink>
                        {!(index === routes.tabs.length - 1 && routes.options.length === 0) && <div className="section-divider" />}
                    </div>
                ))}
                {routes.options?.length > 0 && (
                    <div className={classNames('section', includes(params.tab, ['account-admin', 'blog-admin', 'reports']) && 'active')}>
                        <OutsideClickHandler onOutsideClick={() => setOpened(false)}>
                            <div className="section-item border-0 p-0 position-relative">
                                <div className="px-3 cursor-pointer h-100 d-h-center" onClick={() => setOpened(!opened)}>
                                    <Icon name="fas fa-gear" color="white" size={14} />
                                </div>
                                <Animations.Popup className="popup box-shadow" enter={10} exit={0} opened={opened}>
                                    {routes.options.map((item, index) => (
                                        <Link key={`option${index}`} to={`${rootPath}/${item.name}${query}`} className="dsl-d14" onClick={handleCog(item.name)}>
                                            {item.label}
                                        </Link>
                                    ))}
                                </Animations.Popup>
                            </div>
                        </OutsideClickHandler>
                    </div>
                )}
            </div>
        </div>
    );
};

ProfileHeader.propTypes = {
    avatar: PropTypes.string,
    user: PropTypes.shape({ id: PropTypes.number }),
    company: PropTypes.shape({ id: PropTypes.number }),
    banner: PropTypes.string,
    routes: PropTypes.any,
    query: PropTypes.string,
    editable: PropTypes.bool,
    type: PropTypes.oneOf(['company', 'employee']),
    title: PropTypes.string,
    description: PropTypes.string,
    onChange: PropTypes.func,
    onDropdown: PropTypes.func,
    onSelect: PropTypes.func,
    onConnect: PropTypes.func,
};

export default memo(ProfileHeader);
